import React, { useContext, useEffect, useState } from 'react';
import Dialog, { DialogActionBar } from 'src/components/common/Dialog.redesign';
import Button from 'src/components/controls/forms/redesign/FormButton';
import IOwner from 'src/models/IOwner';
import SearchResult from 'src/models/OwnerSearchResult';
import CreateOwner from 'src/components/project/sections/owners/createOwner/CreateOwner';
import AddEasternOwnership from '../components/AddEasternOwnership';
import OwnerSearch from '../components/OwnerSearch';
import Ownership from 'src/models/tract_detail/Ownership';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import OwnerTracts from '../../owners/OwnerTracts';
import useFetch from 'use-http';
import LeaseGenerationTractDetail from 'src/models/leasing/LeaseGenerationTractDetail';

interface ICreateOwnershipProps {
    tractId: number;
    tractType?: 'Mineral' | 'Surface';
    parentOwnershipId?: number;
    onOwnershipCreated?: (ownership: Ownership) => void;
    onCloseDialog?: () => void;
}

type SearchOwnerState = {
    selectedOwner: SearchResult;
    isLoading: boolean;
    error: string;
};

type Pages = 'Search' | 'New' | 'Ownership';

const CreateOwnershipDialog: React.FC<ICreateOwnershipProps> = (props) => {
    const appContext = useContext<IAppContextState>(AppContext);
    const { get, response } = useFetch();

    const [searchOwnerState, setSearchOwnerState] = React.useState<SearchOwnerState>({
        selectedOwner: undefined,
        isLoading: false,
        error: undefined,
    });
    const [interest, setInterest] = useState<string>('');
    const [additionalTypeId, setAdditionalTypeId] = useState<number>(0);
    const [interestError, setInterestError] = useState<string>('');
    const [page, setPage] = useState<Pages>('Search');
    const [ownerTracts, setOwnerTracts] = useState<Array<LeaseGenerationTractDetail>>([]);

    useEffect(() => {
        if (!!searchOwnerState.selectedOwner) {
            getTractsByOwnerId(searchOwnerState.selectedOwner.id);
        }
    }, [searchOwnerState]);

    const getTractsByOwnerId = async (ownerId: number) => {
        const result = await get(
            `v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/leasegeneration/owner/${ownerId}`
        );

        if (response.ok) {
            setOwnerTracts(result.tracts);
        }
    };

    const saveOwnershipRecord = () => {
        const parsedInterest = Number(interest);

        if (isNaN(parsedInterest)) {
            setInterestError('Invalid Interest Entered');
        } else if (props.parentOwnershipId > 0 && additionalTypeId <= 0) {
            setInterestError('Additional Ownership Type not selected');
        } else {
            const ownership = new Ownership();
            ownership.ownerLookUpId = searchOwnerState.selectedOwner.id;
            ownership.surfaceTractId = props.tractId;
            ownership.decimalInterest = parsedInterest;
            ownership.additionalOwnershipTypeId = props.parentOwnershipId > 0 ? additionalTypeId : null;

            if (props.onOwnershipCreated) {
                props.onOwnershipCreated(ownership);
            }

            props.onCloseDialog();
        }
    };

    const handleOwnerCreated = (owner: IOwner) => {
        const sr = new SearchResult();
        sr.id = owner.id;
        setSearchOwnerState({ ...searchOwnerState, selectedOwner: sr });
        setPage('Ownership');
    };

    const SearchOwnerPage = () => (
        <>
            <div className="flex flex-1" style={{ width: '80rem' }}>
                <OwnerSearch ownerSearchResult={setSearchOwnerState} />
                <div style={{ width: '50%', display: 'flex' }}>
                    <div className="owner-tracts-container">
                        {searchOwnerState.selectedOwner && ownerTracts && <div className="owner-tract-header">Tracts</div>}
                        <OwnerTracts
                            tracts={ownerTracts}
                            clientNumber={appContext.clientNumber}
                            projectNumber={appContext.projectNumber}
                        />
                    </div>
                </div>
            </div>

            <DialogActionBar>
                <Button intent="primary" onClick={() => setPage('New')} text="Create New Owner" />
                <Button
                    intent="primary"
                    disabled={searchOwnerState.selectedOwner === undefined}
                    onClick={() => setPage('Ownership')}
                    text="Use Selected Owner"
                />
                <Button intent="danger" onClick={props.onCloseDialog} text="Cancel" />
            </DialogActionBar>
        </>
    );

    const NewOwnerPage = () => {
        const actions = [
            { id: 1, intent: 'primary', text: 'Create Owner', action: (): void => undefined, isSave: true },
            { id: 2, intent: 'danger', text: 'Back', action: () => setPage('Search'), isSave: false },
            { id: 3, intent: 'danger', text: 'Cancel', action: props.onCloseDialog, isSave: false },
        ];

        return <CreateOwner onOwnerCreated={handleOwnerCreated} actions={actions} />;
    };

    const OwnershipInfoPage = () => (
        <>
            <AddEasternOwnership 
                error={interestError} 
                updateInterest={setInterest} 
                updateAdditionalTypeId={setAdditionalTypeId} 
                ownerId={searchOwnerState.selectedOwner.id} 
                parentOwnershipId={props.parentOwnershipId} 
            />
            <DialogActionBar>
                <Button intent="primary" onClick={saveOwnershipRecord} text="Submit" />
                <Button intent="danger" onClick={() => setPage('Search')} text="Back" />
                <Button intent="danger" onClick={props.onCloseDialog} text="Cancel" />
            </DialogActionBar>
        </>
    );

    return (
        // TODO: anyway to animate height and width changes
        <Dialog title="Create Ownership">
            {page === 'Search' && SearchOwnerPage()}
            {page === 'New' && NewOwnerPage()}
            {page === 'Ownership' && OwnershipInfoPage()}
        </Dialog>
    );
};

export default CreateOwnershipDialog;



// WEBPACK FOOTER //
// ./src/components/project/sections/tract/dialogs/CreateOwnershipDialog.tsx